<template>
    <div class="modal base-modal show-hosting-downgrade-modal">
        <div :class="['modal-content', modalClasses]">
            <base-modal-close @close-modal="closeModal" />

            <transition name="fade" mode="out-in">
                <intro
                    v-if="steps.intro === data.step"
                    @change-step-next="changeStep(steps.copyFeedUrl)"
                />

                <copy-feed-url
                    v-if="steps.copyFeedUrl === data.step"
                    :show="show"
                    next-step-button="All Done! I’m ready to switch"
                    @change-step-prev="changeStep(steps.intro)"
                    @change-step-next="changeStep(steps.feedUrl)"
                >
                    <p class="step-title">
                        Before you switch to a new host.
                    </p>
                    <p class="step-description">
                        Make sure to copy your current Fusebox RSS feed as you’ll most likely need it import your podcast to a new host before turning off Easy Hosting.
                    </p>
                </copy-feed-url>

                <feed-url
                    v-if="steps.feedUrl === data.step"
                    :show="show"
                    @change-step-next="setPreviewShow"
                />

                <update-subscription-handler
                    v-if="steps.showInformation === data.step"
                    v-slot:default="{
                        decrementSubscriptionProducts,
                    }"
                >
                    <show-information
                        :show="previewShow"
                        :form-submitting="isDowngrading"
                        @change-step-prev="changeStep(steps.feedUrl)"
                        @change-step-next="finalizeDowngrade(decrementSubscriptionProducts)"
                    />
                </update-subscription-handler>

                <success
                    v-if="steps.success === data.step"
                    :data="{ mode: 'hosting-cancel' }"
                    @proceed="successRedirect"
                />
            </transition>
        </div>
    </div>
</template>
<script>
import { mapActions, mapState, mapMutations } from 'vuex';
import { steps } from '@/utils/config/showHostingDowngradeModal';
import apiCall from '@/utils/apiCall';
import CopyFeedUrl from '@/components/Shows/CopyFeedUrl';
import FeedUrl from '@/components/Shows/HostingDowngradeModal/FeedUrl';
import Intro from '@/components/Shows/HostingDowngradeModal/Intro';
import ShowInformation from '@/components/Shows/ShowInformation';
import Success from '@/components/Shows/AddShow/Steps/Success';
import UpdateSubscriptionHandler from '@/components/Dashboard/Settings/Billing/UpdateSubscriptionHandler.vue';

export default {
    components: {
        Intro,
        CopyFeedUrl,
        FeedUrl,
        ShowInformation,
        UpdateSubscriptionHandler,
        Success,
    },

    props: {
        show: {
            type: [Boolean, Object],
            default: () => ({}),
        },
    },

    data: () => ({
        previewShow: false,
        isDowngrading: false,
        // intervalProducts: {},
        steps,
    }),

    computed: {
        ...mapState(['userSubscription']),

        ...mapState({
            data: state => state.UIState.showHostingDowngradeModal,
        }),

        /**
         * Returns modal classes.
         *
         * @returns {object} Modal classes.
         */
        modalClasses() {
            return {
                'large-content': [
                    this.steps.intro,
                    this.steps.copyFeedUrl,
                    this.steps.feedUrl,
                    this.steps.showInformation,
                ].includes(this.data.step),
            };
        },

        /**
         * Returns list of Stripe price IDs.
         *
         * @returns {string[]} List of Stripe price IDs.
         */
        priceIds() {
            const priceId = this.show
                ? this.show.plan.prices[this.userSubscription?.interval]?.id
                : null;

            return priceId ? [priceId] : [];
        },
    },

    methods: {
        ...mapActions(['convertShow']),
        ...mapMutations(['changeUIState']),

        /**
         * Closes modal.
         */
        closeModal() {
            this.changeUIState({ showHostingDowngradeModal: { visible: false } });
        },

        /**
         * Changes downgrade step.
         *
         * @param {number} step Step number.
         */
        changeStep(step) {
            this.changeUIState({
                showHostingDowngradeModal: {
                    ...this.data,
                    step,
                },
            });
        },

        /**
         * Sets show for preview.
         *
         * @param {object} previewShow Show for preview.
         */
        setPreviewShow(previewShow) {
            this.previewShow = previewShow;

            this.changeStep(this.steps.showInformation);
        },

        /**
         * Finalizes show downgrade process.
         *
         * @param {Function} decrementSubscription Function decrementing user subcription.
         */
        async finalizeDowngrade(decrementSubscription) {
            this.isDowngrading = true;

            const promises = [];

            if (this.data.plan) {
                promises.push(
                    decrementSubscription(
                        this.userSubscription.dbId,
                        this.priceIds
                    )
                );

                promises.push(
                    this.convertShow({
                        planId: this.data.plan.id,
                    })
                );
            }

            if (this.previewShow) {
                promises.push(
                    apiCall(this, 'put', `/api/shows/${this.show.hash}`, {
                        feedUrl: this.previewShow.feedUrl,
                        hosted: 0,
                    })
                );
            }

            await Promise.all(promises);

            // Load shows after downgrading.
            await this.$store.dispatch('loadShows', { force: true });

            this.isDowngrading = false;

            this.changeStep(this.steps.success);
        },

        /**
         * Redirect after successful show downgrade.
         */
        successRedirect() {
            this.closeModal();

            this.$router.push('/shows');
        },
    },
};
</script>
